import { formatCredits } from "@models/billing";
import CloseIcon from "@mui/icons-material/CloseRounded";
import HourglassIcon from "@mui/icons-material/HourglassEmptyRounded";
import { Box, Button, IconButton, Typography, alpha } from "@mui/material";
import { useEffect, useState } from "react";
import { useBillingPlan, useWallet } from "../hooks/useBilling";
import { UpgradeModal } from "./UpgradeModal";

const DISMISS_KEY = "cf.trialBannerDismissedAt";

/**
 * Site-wide banner. Shows up only when the active scope is on a trial
 * AND it's "running out" — defined as < 72h to expiry, or < 100 credits.
 *
 * Dismiss is sticky for 24h via localStorage; the banner returns the
 * next day so it stays useful without being hostile. Doesn't render at
 * all when not on a trial.
 */
export function TrialBanner() {
  const { data: plan } = useBillingPlan();
  const { data: wallet } = useWallet();
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  useEffect(() => {
    const v = window.localStorage.getItem(DISMISS_KEY);
    setDismissedAt(v ? Number(v) : null);
  }, []);

  if (plan?.id !== "trial") return null;

  const trialEnds = plan.trial_ends_at ? new Date(plan.trial_ends_at) : null;
  const hoursLeft = trialEnds
    ? (trialEnds.getTime() - Date.now()) / 3_600_000
    : Number.POSITIVE_INFINITY;
  const balance = wallet?.balance ?? Number.POSITIVE_INFINITY;

  const expiringSoon = hoursLeft < 72;
  const lowCredits = balance < 100;
  if (!expiringSoon && !lowCredits) return null;

  const stillDismissed =
    dismissedAt && Date.now() - dismissedAt < 24 * 3_600_000;
  if (stillDismissed) return null;

  const message = (() => {
    if (balance === 0) return "Your trial credits are spent.";
    if (lowCredits) return `Only ${formatCredits(balance)} trial credits left.`;
    if (hoursLeft < 24) return "Your trial ends in less than 24 hours.";
    return `Your trial ends in ${Math.ceil(hoursLeft / 24)} days.`;
  })();

  const handleDismiss = () => {
    const now = Date.now();
    window.localStorage.setItem(DISMISS_KEY, String(now));
    setDismissedAt(now);
  };

  return (
    <>
      <Box
        component="output"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1,
          bgcolor: (t) => alpha(t.palette.warning.main, 0.12),
          borderBottom: "1px solid",
          borderColor: "warning.main",
          color: "text.primary",
        }}
      >
        <HourglassIcon sx={{ color: "warning.main", fontSize: 20 }} />
        <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
          {message}{" "}
          <Box component="span" sx={{ color: "text.secondary" }}>
            Upgrade to Pro to keep your project moving.
          </Box>
        </Typography>
        <Button
          variant="contained"
          size="small"
          onClick={() => setUpgradeOpen(true)}
        >
          Upgrade
        </Button>
        <IconButton
          size="small"
          onClick={handleDismiss}
          aria-label="Dismiss trial banner"
          sx={{ color: "text.secondary" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
    </>
  );
}
